/**
 * RequestsTab — the ONE traffic band: a REQUESTS | CONVERSATIONS toggle over
 * the gateway's audit log (TRA-901, unifying TRA-886's two dashboard bands).
 *
 * Two surfaces, one component. The model page passes `modelId` and gets the
 * per-model log (no Model column — noise on a page that IS one model); the
 * dashboard passes none and gets the team-wide log, where every row names
 * its model and team models link to their detail page.
 *
 * `view` is controlled by the parent because the surrounding section heading
 * follows the toggle — "Request log" over requests, "Conversations" over
 * conversations — and only the page owns that heading.
 *
 * Every control on the Requests view lives in the address bar (TRA-1397),
 * under the same param names as the Trajectories page: `range`, `q`,
 * `status`, `fallback`, `model` (team-wide only), `tenant`, `environment`,
 * `traffic`, `session`, `initiator`, `sort`, `page`, plus `view` for the
 * toggle. A filtered view pastes into Slack and opens the same table; a
 * mistyped value falls back to its default.
 */

import { useSearchParams } from 'react-router-dom'
import type {
  DashboardModelRow,
  ListTeamRequestsApiDashboardRequestsGetParams,
  RequestLogItem,
} from '@/api/generated/model'
import { ActiveFilters, FilterPopover } from '@/components/listing/filters'
import { activeFilterCount, metadataFilters, metadataQuery } from '@/components/listing/listingParams'
import { useListingParams } from '@/components/listing/useListingParams'
import { downloadTextFile } from '@/lib/download'
import { RequestDetailSheet } from '@/components/models/home/RequestDetailSheet'
import { RequestLogTable } from '@/components/models/home/RequestLogTable'
import { ConversationsPanel } from '@/components/traces/ConversationsPanel'
import { requestLogCsv } from '@/components/models/home/requestLogCsv'
import {
  REQUEST_PAGE_SIZE,
  REQUEST_RANGE_OPTIONS,
  REQUEST_STATUS_OPTIONS,
  type RequestLogView,
  type RequestStatusFilter,
  withSearchParam,
} from '@/components/models/home/requestLogOptions'
import {
  DEFAULT_REQUEST_RANGE,
  DEFAULT_REQUEST_SORT,
  REQUEST_RANGES,
  parseRequestStatus,
  requestMetadataFilters,
  requestMetadataParams,
  requestSortFields,
} from '@/components/models/home/requestLogParams'
import { useModelRequests } from '@/hooks/useModelRequests'
import { type TeamRequestRange, useTeamRequests } from '@/hooks/useUsage'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Pagination } from '@/components/ui/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'

function downloadCsv(items: RequestLogItem[], range: string): void {
  downloadTextFile(requestLogCsv(items), `requests-${range}.csv`, 'text/csv')
}

interface ViewToggleProps {
  view: RequestLogView
  onView: (next: RequestLogView) => void
}

function ViewToggle({ view, onView }: ViewToggleProps): React.JSX.Element {
  return (
    <div className="flex items-center gap-1">
      {(['requests', 'conversations'] as const).map((v) => (
        <Button
          key={v}
          type="button"
          size="sm"
          variant={view === v ? 'default' : 'outline'}
          aria-pressed={view === v}
          onClick={() => {
            onView(v)
          }}
        >
          {v === 'requests' ? 'Requests' : 'Conversations'}
        </Button>
      ))}
    </div>
  )
}

interface RequestsTabProps {
  /** Scope to one model; omit on the dashboard for the whole team. */
  modelId?: string
  /** Team-wide only: the team's models, for resolving Model cells to links. */
  models?: DashboardModelRow[]
  /** Team-wide only: prefix on every canonical model id in the audit log. */
  gatewayTeamSlug?: string | null
  /** Controlled so the page's section heading can follow the toggle. */
  view: RequestLogView
  onView: (next: RequestLogView) => void
  /** The page this tab is on ("dashboard", a model's display name), for
      the Conversation page's back arrow (TRA-1412). */
  pageName: string
}

export function RequestsTab({
  modelId,
  models = [],
  gatewayTeamSlug = null,
  view,
  onView,
  pageName,
}: RequestsTabProps): React.JSX.Element {
  const teamWide = modelId === undefined
  const listing = useListingParams({
    ranges: REQUEST_RANGES,
    defaultRange: DEFAULT_REQUEST_RANGE,
    sortFields: requestSortFields(teamWide),
    defaultSort: DEFAULT_REQUEST_SORT,
    metadataParams: requestMetadataParams(teamWide),
  })
  const { searchParams, updateParams, debouncedSearch, idSearch, effectiveRange, sort, page } =
    listing
  const status = parseRequestStatus(searchParams.get('status'))
  const fallbackOnly = searchParams.get('fallback') === '1'
  const metadataSpecs = requestMetadataFilters(teamWide)
  const metadata = metadataQuery(searchParams, requestMetadataParams(teamWide))
  const filtered =
    debouncedSearch !== '' || status !== 'all' || fallbackOnly || Object.keys(metadata).length > 0
  // The open row's trace key lives in the URL too (TRA-1412), so a reload
  // or a shared link reopens the same request.
  const [, setSearchParams] = useSearchParams()
  const openTraceKey = searchParams.get('trace')

  function setStatus(value: RequestStatusFilter | 'all'): void {
    updateParams((params) => {
      if (value === 'all') params.delete('status')
      else params.set('status', value)
    })
  }

  // The uptime wall's drill-down writes this param directly (TRA-923); it
  // drops the page the same way the button does.
  function setFallbackOnly(next: boolean): void {
    updateParams((params) => {
      if (next) params.set('fallback', '1')
      else params.delete('fallback')
    })
  }

  function setOpenTraceKey(next: string | null): void {
    setSearchParams((prev) => withSearchParam(prev, 'trace', next), { replace: true })
  }

  const params: ListTeamRequestsApiDashboardRequestsGetParams = {
    range: effectiveRange,
    ...(debouncedSearch !== '' ? { q: debouncedSearch } : {}),
    ...(status !== 'all' ? { status } : {}),
    ...(fallbackOnly ? { fallback: true } : {}),
    ...metadata,
    sort: sort.field,
    dir: sort.desc ? 'desc' : 'asc',
    limit: REQUEST_PAGE_SIZE,
    offset: (page - 1) * REQUEST_PAGE_SIZE,
  }
  // Both hooks always run (hooks can't be conditional); each disables itself
  // unless it is the active one for this surface AND the requests view is
  // actually showing — otherwise every page load fires a query for a table
  // nobody sees.
  const showRequests = view === 'requests'
  const modelQuery = useModelRequests({ modelId, params, enabled: showRequests })
  const teamQuery = useTeamRequests({ params, enabled: teamWide && showRequests })
  const { data, isLoading, isError } = teamWide ? teamQuery : modelQuery

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / REQUEST_PAGE_SIZE))
  // Dropdown options are the distinct values on this page.
  const metadataOptions = metadataFilters(metadataSpecs, searchParams, items)
  const activeCount = activeFilterCount(metadataOptions)

  // Canonical `<teamSlug>/<slug>` → model id. Built per render off two small
  // arrays; memoising this would cost more than it saves.
  const teamModelIds = new Map(
    gatewayTeamSlug === null
      ? []
      : models.map((model) => [`${gatewayTeamSlug}/${model.slug}`, model.id]),
  )

  if (view === 'conversations') {
    return (
      <ConversationsPanel
        controls={<ViewToggle view={view} onView={onView} />}
        modelId={modelId}
        pageName={pageName}
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <ViewToggle view={view} onView={onView} />

          <Input
            value={listing.search}
            onChange={(event) => {
              listing.setSearch(event.target.value)
            }}
            placeholder="Search model, trace, tenant, session…"
            aria-label="Search requests"
            className="h-8 w-[320px] font-mono text-xs"
          />

          {/* A pasted run id or trace key names one request of any age, so
              that search covers all time and the picker is held. */}
          <Select
            value={effectiveRange}
            disabled={idSearch}
            onValueChange={(v) => {
              listing.setRange(v as TeamRequestRange)
            }}
          >
            <SelectTrigger
              aria-label="Time range"
              title={idSearch ? 'An id search covers all time.' : undefined}
              className="h-8 w-auto min-w-[130px] gap-1 text-xs"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REQUEST_RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value} className="text-xs">
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Chips rather than a second dropdown: four mutually exclusive values
              the reader flips between while scanning. */}
          <div className="flex items-center gap-1">
            {REQUEST_STATUS_OPTIONS.map((o) => (
              <Button
                key={o.value}
                type="button"
                size="sm"
                variant={status === o.value ? 'default' : 'outline'}
                aria-pressed={status === o.value}
                onClick={() => {
                  setStatus(o.value)
                }}
              >
                {o.label}
              </Button>
            ))}
          </div>

          {/* Separate axis from the status chips: a fallback is a 200, so
              "5xx" and "served by fallback" compose rather than compete. */}
          <Button
            type="button"
            size="sm"
            variant={fallbackOnly ? 'default' : 'outline'}
            aria-pressed={fallbackOnly}
            onClick={() => { setFallbackOnly(!fallbackOnly) }}
          >
            Fallbacks
          </Button>

          <FilterPopover
            filters={metadataOptions}
            onMetadata={listing.setMetadata}
            emptyMessage="No filters apply to these requests."
          />

          <Button
            type="button"
            size="sm"
            variant="outline"
            className="ml-auto"
            disabled={items.length === 0}
            onClick={() => {
              downloadCsv(items, effectiveRange)
            }}
          >
            Export CSV
          </Button>
        </div>
        {activeCount > 0 && (
          <ActiveFilters
            filters={metadataOptions}
            onMetadata={listing.setMetadata}
            onClear={listing.clearMetadata}
          />
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive">Failed to load requests.</p>
      ) : items.length === 0 ? (
        <>
          <EmptyState
            title="No requests"
            description={
              total > 0
                ? 'This page is past the end of the window. It moved while you were reading.'
                : filtered
                  ? 'No requests match these filters.'
                  : teamWide
                    ? 'No requests through the gateway in the selected window.'
                    : 'No requests to this model in the selected window.'
            }
          />
          {/* Keep pagination when the WINDOW has rows but this page does not,
              or the reader is stranded on a dead page with no way back. */}
          {total > 0 ? (
            <Pagination
              currentPage={page}
              totalPages={pageCount}
              onPrevious={() => { listing.setPage(Math.max(1, page - 1)) }}
              onNext={() => { listing.setPage(Math.min(pageCount, page + 1)) }}
            />
          ) : null}
        </>
      ) : (
        <>
          <RequestLogTable
            items={items}
            showModel={teamWide}
            teamModelIds={teamModelIds}
            onOpenTrace={setOpenTraceKey}
            sort={sort}
            onSort={listing.setSort}
          />

          <Pagination
            currentPage={page}
            totalPages={pageCount}
            onPrevious={() => { listing.setPage(Math.max(1, page - 1)) }}
            onNext={() => { listing.setPage(Math.min(pageCount, page + 1)) }}
          />
        </>
      )}

      <RequestDetailSheet
        traceKey={openTraceKey}
        pageName={pageName}
        onClose={() => { setOpenTraceKey(null) }}
      />
    </div>
  )
}
